// Pure mention text helpers shared by web and mobile. The components are
// written twice (packages/shared has no .tsx); this logic is written once.
import { MENTION_ALL_HANDLE } from '../constants/message-reactions.js';
import { isValidUsername } from './username.js';
// Username-legal run (mirrors USERNAME_PATTERN's inner charset, includes '-').
const HANDLE_CHAR = /[A-Za-z0-9._-]/;
// Chars that, immediately before '@', mean it is NOT the start of a mention
// (an email local part, or mid-token) — excludes the '@'.
const NON_BOUNDARY_BEFORE_AT = /[A-Za-z0-9._@-]/;
/**
 * Locate every `@all` in the body, using the SAME boundary rule as a user
 * handle: not preceded by a handle char or '@' (so `foo@all` is an email tail,
 * not a mention), and not followed by one (so `@allan` is a person).
 * Case-insensitive, because the composer does not force lowercase.
 *
 * This exists in shared — rather than only server-side — so the notification
 * flag and the client highlight can never disagree about what `@all` means.
 *
 * @returns Spans in source order, `length` covering '@' + the handle.
 */
export function findAllMentionSpans(content) {
    if (!content)
        return [];
    const re = new RegExp(`@${MENTION_ALL_HANDLE}`, 'gi');
    const spans = [];
    let m;
    while ((m = re.exec(content)) !== null) {
        const prev = m.index > 0 ? content[m.index - 1] : '';
        if (prev && NON_BOUNDARY_BEFORE_AT.test(prev))
            continue;
        const next = content[m.index + m[0].length] ?? '';
        if (next && HANDLE_CHAR.test(next))
            continue;
        spans.push({ start: m.index, length: m[0].length });
    }
    return spans;
}
/**
 * Given the full text and the caret position, return the active @mention the
 * user is typing, or null. Active means: walking left from the caret we hit an
 * '@' through only handle-legal chars, and that '@' sits at a word boundary
 * (start of text or after whitespace/punctuation, never inside an email).
 */
export function findActiveMentionQuery(text, caret) {
    const pos = Math.max(0, Math.min(caret, text.length));
    let i = pos - 1;
    while (i >= 0 && HANDLE_CHAR.test(text[i]))
        i--;
    if (i < 0 || text[i] !== '@')
        return null;
    const before = i > 0 ? text[i - 1] : '';
    if (before && NON_BOUNDARY_BEFORE_AT.test(before))
        return null;
    return { query: text.slice(i + 1, pos), start: i, end: pos };
}
/**
 * Replace the active @mention query span with "@username " and return the new
 * text plus the caret position after the inserted trailing space.
 */
export function applyMentionSelection(text, active, username) {
    const insert = `@${username} `;
    const next = text.slice(0, active.start) + insert + text.slice(active.end);
    return { text: next, caret: active.start + insert.length };
}
/**
 * Extract @handles from free text. Ignores email addresses (the '@' in
 * "name@host" is preceded by an alnum/dot, which fails the boundary check).
 * Returns validated, lowercased usernames with their content offsets, in order
 * of appearance, deduplicated by handle (first occurrence wins).
 *
 * THIS IS THE CANONICAL HANDLE GRAMMAR. The API's `extractHandles` delegates
 * here and the composers highlight from here, so what lights up while typing
 * cannot disagree with what the server stores. Note the trailing-separator trim:
 * "@ajesh." yields handle "ajesh" with length 6, excluding the full stop.
 */
export function extractMentionHandles(content) {
    const out = [];
    const seen = new Set();
    // Match '@' + a run of username-legal characters; validate/trim afterwards.
    const re = /@([A-Za-z0-9._-]+)/g;
    let m;
    while ((m = re.exec(content)) !== null) {
        const at = m.index;
        const prev = at > 0 ? content[at - 1] : '';
        if (NON_BOUNDARY_BEFORE_AT.test(prev))
            continue;
        // Trim trailing separators ("@ajesh." at a sentence end → "ajesh").
        const raw = m[1].replace(/[._-]+$/, '');
        const handle = raw.toLowerCase();
        if (!isValidUsername(handle))
            continue;
        if (seen.has(handle))
            continue;
        seen.add(handle);
        out.push({ handle, start: at, length: handle.length + 1 });
    }
    return out;
}
/**
 * Segment text the user is CURRENTLY TYPING, for a live composer highlight.
 *
 * Unlike `segmentMentions` there are no stored offsets yet, so a handle is
 * resolved against the candidate list instead: only a handle that will actually
 * notify someone lights up. A typo stays plain, which is the point — you can
 * see before sending that it resolves to nobody.
 */
export function segmentTypedMentions(content, options) {
    if (!content)
        return [];
    const known = new Set(options.usernames.map((u) => u.toLowerCase()));
    const spans = extractMentionHandles(content)
        .filter((h) => known.has(h.handle))
        .map((h) => ({ start: h.start, length: h.length, handle: h.handle }));
    if (options.allowAll) {
        for (const a of findAllMentionSpans(content)) {
            const overlaps = spans.some((s) => a.start < s.start + s.length && a.start + a.length > s.start);
            if (!overlaps)
                spans.push({ ...a, handle: null });
        }
    }
    spans.sort((a, b) => a.start - b.start);
    const segments = [];
    let cursor = 0;
    for (const s of spans) {
        if (s.start < cursor)
            continue;
        if (s.start > cursor)
            segments.push({ type: 'text', text: content.slice(cursor, s.start) });
        const text = content.slice(s.start, s.start + s.length);
        segments.push(s.handle
            ? { type: 'mention', text, userId: '', username: s.handle, displayName: null }
            : { type: 'mention-all', text });
        cursor = s.start + s.length;
    }
    if (cursor < content.length)
        segments.push({ type: 'text', text: content.slice(cursor) });
    return segments;
}
/**
 * Split content into text and mention segments for rendering. Mentions are
 * spliced at their stored offsets and displayed with the CURRENT handle
 * (rename-proof). A row whose slice no longer starts with '@' is treated as
 * desynced and swept back into plain text rather than corrupting the render.
 *
 * With `options.mentionsAll`, any `@all` in the body is spliced too. Real
 * mentions are server-authoritative, so they win any overlap.
 */
export function segmentMentions(content, mentions, options) {
    if (!content)
        return [];
    const userSpans = [...(mentions ?? [])]
        .filter((m) => Number.isInteger(m.start_offset) && m.length > 0)
        .map((m) => ({ start: m.start_offset, length: m.length, ref: m }));
    const allSpans = options?.mentionsAll
        ? findAllMentionSpans(content)
            // A real mention row wins: drop any @all span that overlaps one.
            .filter((a) => userSpans.every((u) => a.start >= u.start + u.length || a.start + a.length <= u.start))
            .map((a) => ({ ...a, ref: null }))
        : [];
    const sorted = [...userSpans, ...allSpans].sort((a, b) => a.start - b.start);
    const segments = [];
    let cursor = 0;
    for (const span of sorted) {
        const m = span.ref;
        if (span.start < cursor)
            continue; // overlap — skip
        const slice = content.slice(span.start, span.start + span.length);
        if (!slice.startsWith('@'))
            continue; // desync — leave as plain text
        if (span.start > cursor) {
            segments.push({ type: 'text', text: content.slice(cursor, span.start) });
        }
        segments.push(m
            ? {
                type: 'mention',
                text: `@${m.username}`,
                userId: m.user_id,
                username: m.username,
                displayName: m.display_name,
            }
            : { type: 'mention-all', text: slice });
        cursor = span.start + span.length;
    }
    if (cursor < content.length) {
        segments.push({ type: 'text', text: content.slice(cursor) });
    }
    return segments;
}
/** How many handles to name before collapsing the rest into "+N more". */
const DROPPED_PREVIEW_LIMIT = 3;
/**
 * Compose the composer-facing warning for @handles that were written but never
 * notified. Deliberately neutral about the cause: a dropped handle may be a real
 * user without task access, a typo matching nobody, or an overflow past the
 * per-body mention cap, and the server does not distinguish them.
 *
 * @param handles - Bare handles (no leading '@'), as returned in `droppedMentions`
 * @returns A single sentence, or '' when nothing was dropped
 */
export function formatDroppedMentionsMessage(handles) {
    if (handles.length === 0)
        return '';
    const shown = handles.slice(0, DROPPED_PREVIEW_LIMIT).map((h) => `@${h}`);
    const overflow = handles.length - shown.length;
    const names = overflow > 0 ? `${shown.join(', ')} +${overflow} more` : shown.join(', ');
    return `Couldn't notify ${names} — check the handle spelling or their access to this task.`;
}
//# sourceMappingURL=mention-text.js.map